import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackgroundVisuals from "../components/BackgroundVisuals";

const ProcessPage = () => {
  const headerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const detailedProcesses = [
    {
      phase: "01",
      title: "Discover & Strategize",
      subtitle: "Strategic AI Planning & Assessment",
      icon: "/images/discovery.png",
      color: "from-blue-800 to-black",
      deliverables: "AI Strategy Document, Technical Roadmap, ROI Analysis",
      description: "We begin with a comprehensive analysis of your business challenges, existing infrastructure, and AI opportunity mapping to create a strategic foundation.",
      steps: [
        {
          name: "Business Analysis",
          details: "Deep-dive stakeholder interviews and process mapping to identify pain points and opportunities",
          tools: ["Process Mining", "Stakeholder Interviews", "Business Process Analysis"]
        },
        {
          name: "AI Readiness Assessment", 
          details: "Evaluate current data infrastructure, team capabilities, and technological readiness",
          tools: ["Data Audit", "Infrastructure Assessment", "Team Skill Analysis"]
        }
      ]
    },
    {
      phase: "02",
      title: "Co-Develop & Co-Create",
      subtitle: "Collaborative AI Model Development",
      icon: "/images/enablement.png",
      color: "from-[#952877] to-black",
      deliverables: "AI Models, APIs, Documentation, Training Materials",
      description: "Collaborative development phase where we design, build, and train AI models using cutting-edge algorithms and your domain expertise.",
      steps: [
        {
          name: "Data Preparation",
          details: "Clean, transform, and structure data for optimal AI model performance and accuracy",
          tools: ["Data Pipelines", "Feature Engineering", "Data Validation"]
        },
        {
          name: "Model Architecture",
          details: "Design optimal AI model architecture based on use case requirements and performance criteria",
          tools: ["TensorFlow", "PyTorch", "Custom Algorithms"]
        }
      ]
    },
    {
      phase: "03",
      title: "Deploy & Scale",
      subtitle: "Production Deployment & Optimization",
      icon: "/images/deployment.png",
      color:"from-emerald-800 to-black",
      deliverables: "Production System, Monitoring Dashboard, Support Documentation",
      description: "Production deployment with comprehensive monitoring, optimization, and continuous improvement frameworks to ensure long-term success.",
      steps: [
        {
          name: "Production Rollout",
          details: "Deploy AI models to production with proper security, scalability, and reliability measures",
          tools: ["Docker", "Kubernetes", "CI/CD Pipelines"]
        },
        {
          name: "Monitoring & Tuning",
          details: "Implement systems to track model performance, usage, and business impact",
          tools: ["Performance Dashboards", "Alert Systems", "Model Tuning"]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white selection:bg-[#952877]/10 selection:text-[#952877]">
      <BackgroundVisuals />
      
      {/* --- HERO HEADER --- */}
      <section ref={headerRef} className="pt-32 pb-20 relative z-10">
        <div className="max-w-6xl mx-auto px-6 text-center py-10">
          {/* <div className="inline-block px-4 py-1.5 mb-8 rounded-full border border-[#952877]/20 bg-[#952877]/5">
             <span className="text-[10px] font-black tracking-[0.2em] uppercase text-[#952877]">Lifecycle Methodology</span>
          </div> */}
          <h1 className="text-5xl md:text-8xl font-black text-[#17091a] mb-8 tracking-tighter leading-tight">
            Our AI Dev <span className="bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#18141e] bg-clip-text text-transparent  px-2">Process</span>
          </h1>
          <p className="text-xl text-gray-500 max-w-3xl mx-auto font-light leading-relaxed">
            A comprehensive, systematic approach to developing robust, scalable, and ethical AI solutions that drive real business results.
          </p>
        </div>
      </section>

      {/* --- PROCESS LIFECYCLE DIAGRAM --- */}
      

      {/* --- DETAILED PHASES --- */}
      <section className="pb-32 relative z-10 px-6">
        <div className="max-w-6xl mx-auto space-y-24">
          {detailedProcesses.map((process, index) => (
            <div key={index} className="group">
              <div className="bg-white/40 backdrop-blur-xl rounded-[4rem] border border-white/60 overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)] transition-all duration-700 hover:shadow-[0_48px_96px_-20px_rgba(0,0,0,0.1)]">
                
                {/* Header Strip */}
                <div className={`bg-gradient-to-r ${process.color} p-10 md:p-16 text-white`}>
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-10">
                    <div className="w-24 h-24 bg-white/20 rounded-3xl backdrop-blur-md flex items-center justify-center border border-white/30 shadow-2xl group-hover:shadow-white group-hover:scale-110 transition-transform duration-700">
                      <img src={process.icon} alt={process.title} className="w-14 h-14 object-contain" />
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] font-black uppercase tracking-[0.4em] opacity-70 mb-4">Phase / {process.phase}</div>
                      <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 leading-none">{process.title}</h2>
                      <p className="text-xl font-light italic opacity-90 mb-6">{process.subtitle}</p>
                      <div className="inline-block bg-white/10 rounded-2xl p-6 border border-white/10 backdrop-blur-sm">
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] block mb-2 opacity-60">Deliverables</span>
                        <p className="text-sm font-bold tracking-tight">{process.deliverables}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Steps Grid */}
                <div className="p-10 md:p-20">
                  <div className="grid md:grid-cols-2 gap-12">
                    {process.steps.map((step, sIdx) => (
                      <div key={sIdx} className="relative pl-12">
                        <div className={`absolute left-0 top-0 w-8 h-8 rounded-xl bg-gradient-to-br ${process.color} flex items-center justify-center text-white text-[10px] font-black shadow-lg shadow-indigo-500/20`}>
                          0{sIdx + 1}
                        </div>
                        <h4 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight">{step.name}</h4>
                        <p className="text-sm text-gray-500 font-medium leading-relaxed mb-8">{step.details}</p>
                        
                        <div className="flex flex-wrap gap-2">
                          {step.tools.map((tool, tIdx) => (
                            <span key={tIdx} className="px-3 py-1 bg-gray-50 rounded-lg text-[9px] font-black text-[#17091a]/40 uppercase tracking-widest border border-gray-100">
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section className="pb-32 px-6">
        <div className="max-w-5xl mx-auto relative group overflow-hidden rounded-[4rem] bg-[#17091a] p-16 md:p-24 text-center">
           <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#952877]/20 blur-[100px] rounded-full"></div>
           <div className="relative z-10">
              <h2 className="text-4xl md:text-7xl font-black text-white mb-10 tracking-tighter leading-tight italic">Initiate <br/> Transformation</h2>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
                <Link 
                  to="/"
                  className="px-10 py-5 bg-white/10 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] backdrop-blur-md border border-white/10 hover:bg-white/20 transition-all"
                >
                  Return Home
                </Link>
                <Link 
                  to="/contact"
                  className="px-10 py-5 bg-white text-[#17091a] rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] hover:bg-[#952877] hover:text-white transition-all duration-500 shadow-2xl"
                >
                  Start Project
                </Link>
              </div>
           </div>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;